import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, map, switchMap } from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class S3Service {

  constructor(
    private readonly http: HttpClient
  ) { }

  /**
     * Generate presigned URL for file download with specific locale
     */
  getFileDownloadUrl(fileKey: string, apiUrl: string): Observable<string> {

    let params = new HttpParams().set('fileKey', fileKey);
    return this.http.get<{ url: string }>(apiUrl, { params }).pipe(
      map(response => response.url)
    );
  }

  /**
   * Generate presigned URL for file upload with optional contract ID and fileKey
   */
  generateUploadUrl(contractId: string, fileKey: string, apiUrl: string): Observable<{ url: string; key: string }> {

    // Use PUT with fileKey in body (contractId may be empty for initial upload)
    const body: { fileKey: string; contractId?: string } = { fileKey };
    if (contractId) {
      body.contractId = contractId;
    }
    return this.http.put<{ url: string; key: string }>(apiUrl, body);
  }


  /**
     * Upload file to presigned URL
     */
  uploadFile(url: string, file: File, fileKey: string): Observable<any> {

    // For S3 presigned URLs, we need to bypass Angular interceptors
    // Use XMLHttpRequest directly for S3 uploads to avoid authentication header conflicts
    return new Observable(observer => {
      const xhr = new XMLHttpRequest();

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next({ success: true, status: xhr.status });
          observer.complete();
        } else {
          observer.error(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        observer.error(new Error('Upload failed due to network error'));
      };

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;

        }
      };

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.send(file);
    });
  }

  /**
   * Upload an image to S3 bucket by first generating upload URL then uploading the file
   */
  uploadImageToS3(
    file: File,
    fileKey: string,
    apiUrl: string,
  ): Observable<{ success: boolean; fileKey: string; status: number }> {

    return this.generateUploadUrl('', fileKey, apiUrl).pipe(
      switchMap(response => {
        return this.uploadFile(response.url, file, response.key).pipe(
          map(uploadResult => ({
            success: uploadResult.success,
            fileKey: response.key,
            status: uploadResult.status
          }))
        );
      })
    );
  }
}
